/**
 * CetusEd Admin Console — SIS Sync Configuration
 *
 * COMPLIANCE: FERPA §1232g(b) — Student record accuracy depends on correct
 *   SIS sync configuration. The SIS provider, sync interval, and last sync
 *   status are displayed. Sync errors must be investigated promptly.
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSISConfig, useUpdateSISConfig } from '../../api/hooks';

export const SISConfig: React.FC = () => {
  const [districtId, setDistrictId] = useState('');
  const { data: config, isLoading, error } = useSISConfig(districtId);
  const updateConfig = useUpdateSISConfig();

  const [editMode, setEditMode] = useState(false);
  const [provider, setProvider] = useState('');
  const [syncInterval, setSyncInterval] = useState(60);

  const handleDistrictSelect = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setDistrictId(e.target.value);
    setEditMode(false);
  }, []);

  const handleEdit = useCallback(() => {
    if (config) {
      setProvider(config.provider || '');
      setSyncInterval(config.syncIntervalMinutes || 60);
    }
    setEditMode(true);
  }, [config]);

  const handleSave = useCallback(() => {
    if (!districtId) return;
    // mutate() routes failures into updateConfig.isError, which is rendered
    // below — no swallowed rejection, no invisible failure.
    updateConfig.mutate(
      {
        districtId,
        config: { provider, syncIntervalMinutes: syncInterval, enabled: true },
      },
      { onSuccess: () => setEditMode(false) },
    );
  }, [districtId, provider, syncInterval, updateConfig]);

  if (isLoading) return <div className="admin-loading">Loading SIS config...</div>;

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/integrations">Integrations</Link> / SIS Sync
      </nav>

      <div className="page-header">
        <h1>SIS Sync Configuration</h1>
      </div>

      <div className="form-field">
        <label htmlFor="sis-district">Select District</label>
        <select id="sis-district" value={districtId} onChange={handleDistrictSelect}>
          <option value="">Select a district...</option>
        </select>
      </div>

      {districtId && config && (
        <div className="detail-card">
          <h3>Sync Configuration</h3>
          {editMode ? (
            <div className="admin-form">
              <div className="form-field">
                <label htmlFor="sis-provider">Provider</label>
                <select id="sis-provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
                  <option value="oneroster">OneRoster 1.2</option>
                  <option value="clever">Clever</option>
                  <option value="manual">Manual Upload</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="sis-interval">Sync Interval (minutes)</label>
                <input id="sis-interval" type="number" value={syncInterval} onChange={(e) => setSyncInterval(Number(e.target.value))} min={15} max={1440} />
              </div>
              {updateConfig.isError && (
                <div className="admin-error" role="alert">
                  Failed to save SIS configuration. Please try again.
                </div>
              )}
              <div className="form-actions">
                <button className="admin-btn admin-btn--primary" onClick={handleSave} disabled={updateConfig.isPending}>
                  Save Configuration
                </button>
                <button className="admin-btn admin-btn--ghost" onClick={() => setEditMode(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <dl>
                <dt>Provider</dt>
                <dd>{config.provider || 'Not configured'}</dd>
                <dt>Status</dt>
                <dd><span className={`status-badge status-badge--${config.enabled ? 'active' : 'deactivated'}`}>
                  {config.enabled ? 'Enabled' : 'Disabled'}
                </span></dd>
                <dt>Sync Interval</dt>
                <dd>{config.syncIntervalMinutes} minutes</dd>
                <dt>Last Sync</dt>
                <dd>{config.lastSyncAt ? new Date(config.lastSyncAt).toLocaleString() : 'Never'}</dd>
                <dt>Last Status</dt>
                <dd>{config.lastSyncStatus || '—'}</dd>
              </dl>
              <button className="admin-btn admin-btn--secondary" onClick={handleEdit}>
                Edit Configuration
              </button>
            </>
          )}
        </div>
      )}

      {districtId && error && (
        <div className="admin-error" role="alert">
          Unable to load SIS configuration.
        </div>
      )}
    </div>
  );
};
